import { useState, useEffect, useCallback } from "react";
import { ShieldCheck, ShieldOff, Copy, Check, KeyRound } from "lucide-react";
import { adminAxios, formatDetail } from "../../lib/admin";

export default function AdminSecurity() {
  const [status, setStatus] = useState(null);
  const [phase, setPhase] = useState("status");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdCode, setPwdCode] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [pwdOk, setPwdOk] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);

  const loadStatus = useCallback(() => {
    adminAxios
      .get("/auth/2fa/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setError("No se pudo cargar el estado de seguridad."));
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const beginSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminAxios.post("/auth/2fa/setup");
      setSetup(data);
      setPhase("setup");
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || "No se pudo iniciar la configuración.");
    } finally {
      setLoading(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await adminAxios.post("/auth/2fa/confirm", { code: code.trim() });
      setRecoveryCodes(data.recovery_codes);
      setPhase("recovery");
      setCode("");
      loadStatus();
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || "Código no válido.");
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await adminAxios.post("/auth/2fa/disable", { password: disablePwd, code: disableCode.trim() });
      setDisablePwd("");
      setDisableCode("");
      setPhase("status");
      loadStatus();
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || "No se pudo desactivar el 2FA.");
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdError("");
    setPwdOk(false);
    if (newPwd !== confirmPwd) {
      setPwdError("La confirmación no coincide con la nueva contraseña.");
      return;
    }
    if (newPwd.length < 12 || !/[a-zA-Z]/.test(newPwd) || !/\d/.test(newPwd)) {
      setPwdError("La nueva contraseña debe tener al menos 12 caracteres e incluir letras y números.");
      return;
    }
    setPwdLoading(true);
    try {
      const payload = { current_password: curPwd, new_password: newPwd };
      if (status?.enabled) payload.code = pwdCode.trim();
      await adminAxios.post("/auth/change-password", payload);
      setCurPwd("");
      setNewPwd("");
      setConfirmPwd("");
      setPwdCode("");
      setPwdOk(true);
    } catch (err) {
      setPwdError(formatDetail(err?.response?.data?.detail) || "No se pudo cambiar la contraseña.");
    } finally {
      setPwdLoading(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!status && !error) return <p className="tech-label">CARGANDO SEGURIDAD…</p>;

  return (
    <div data-testid="admin-security" className="max-w-2xl space-y-8">
      <div>
        <h2 className="font-serif font-bold text-2xl text-onix-white">Seguridad de la cuenta</h2>
        <p className="text-sm text-onix-gray mt-2 leading-relaxed">
          Verificación en dos pasos (2FA) con app de autenticación: Google Authenticator, Microsoft Authenticator, Authy o 1Password.
        </p>
      </div>

      {error && (
        <p data-testid="admin-security-error" role="alert" className="text-xs font-bold text-onix-white border border-dashed border-onix-silver p-3">
          {error}
        </p>
      )}

      {status && phase === "status" && !status.enabled && (
        <div className="border border-onix-line bg-onix-black2 p-6 space-y-5">
          <div className="flex items-center gap-3">
            <ShieldOff size={18} className="text-onix-gray" aria-hidden="true" />
            <p data-testid="admin-2fa-status" className="text-sm font-bold text-onix-silver">2FA desactivado</p>
          </div>
          <p className="text-sm text-onix-gray leading-relaxed">
            Activa la verificación en dos pasos para añadir una capa extra de protección al panel. Tras la contraseña se pedirá un código de 6 dígitos generado por tu app.
          </p>
          <button data-testid="admin-2fa-enable" onClick={beginSetup} disabled={loading} className="btn-onix-primary disabled:opacity-60">
            {loading ? "Generando…" : "Activar 2FA"}
          </button>
        </div>
      )}

      {phase === "setup" && setup && (
        <form onSubmit={confirmSetup} className="border border-onix-line bg-onix-black2 p-6 space-y-6">
          <p className="text-sm font-bold text-onix-white">1 · Escanea este código QR con tu app de autenticación</p>
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <img
              data-testid="admin-2fa-qr"
              src={setup.qr_data_url}
              alt="Código QR de configuración 2FA"
              className="w-44 h-44 bg-white p-2"
            />
            <div className="space-y-3">
              <p className="text-xs text-onix-gray leading-relaxed">Si no puedes escanear el QR, introduce esta clave manualmente en la app:</p>
              <code data-testid="admin-2fa-secret" className="block text-xs text-onix-white border border-onix-line p-3 break-all tracking-widest">
                {setup.secret}
              </code>
            </div>
          </div>
          <p className="text-sm font-bold text-onix-white">2 · Introduce el código de 6 dígitos que muestra la app</p>
          <input
            data-testid="admin-2fa-confirm-code"
            type="text"
            inputMode="numeric"
            maxLength={6}
            className="onix-input max-w-[220px] tracking-[0.3em]"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="000000"
            required
          />
          <div className="flex gap-3">
            <button data-testid="admin-2fa-confirm-submit" type="submit" disabled={loading || code.length !== 6} className="btn-onix-primary disabled:opacity-60">
              {loading ? "Verificando…" : "Confirmar y activar"}
            </button>
            <button
              data-testid="admin-2fa-cancel-setup"
              type="button"
              className="btn-onix-secondary"
              onClick={() => {
                setPhase("status");
                setSetup(null);
                setCode("");
                setError("");
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {phase === "recovery" && (
        <div className="border border-onix-white bg-onix-black2 p-6 space-y-5">
          <div className="flex items-center gap-3">
            <ShieldCheck size={18} className="text-onix-white" aria-hidden="true" />
            <p className="text-sm font-bold text-onix-white">2FA activado correctamente</p>
          </div>
          <p className="text-sm text-onix-silver leading-relaxed">
            Guarda estos códigos de recuperación en un lugar seguro. Cada uno solo puede usarse una vez y <strong className="text-onix-white">no volverán a mostrarse</strong>. Te permitirán acceder si pierdes el móvil.
          </p>
          <pre data-testid="admin-2fa-recovery-codes" className="text-sm text-onix-white border border-onix-line p-4 tracking-widest leading-loose">
            {recoveryCodes.join("\n")}
          </pre>
          <div className="flex gap-3">
            <button data-testid="admin-2fa-copy-codes" onClick={copyCodes} className="btn-onix-secondary inline-flex items-center gap-2">
              {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
              {copied ? "Copiados" : "Copiar códigos"}
            </button>
            <button
              data-testid="admin-2fa-recovery-done"
              onClick={() => {
                setRecoveryCodes([]);
                setPhase("status");
              }}
              className="btn-onix-primary"
            >
              He guardado los códigos
            </button>
          </div>
        </div>
      )}

      {status && phase === "status" && status.enabled && (
        <div className="space-y-6">
          <div className="border border-onix-line bg-onix-black2 p-6 space-y-3">
            <div className="flex items-center gap-3">
              <ShieldCheck size={18} className="text-onix-white" aria-hidden="true" />
              <p data-testid="admin-2fa-status" className="text-sm font-bold text-onix-white">2FA activado</p>
            </div>
            <p className="text-sm text-onix-gray">
              Códigos de recuperación disponibles: <span data-testid="admin-2fa-recovery-left" className="text-onix-white font-bold">{status.recovery_codes_left}</span>
            </p>
            <p className="text-xs text-onix-gray leading-relaxed">
              Para regenerar los códigos de recuperación, desactiva y vuelve a activar el 2FA.
            </p>
          </div>

          <form onSubmit={disable2FA} className="border border-onix-line bg-onix-black2 p-6 space-y-5">
            <p className="text-sm font-bold text-onix-white">Desactivar 2FA</p>
            <p className="text-xs text-onix-gray leading-relaxed">Requiere tu contraseña y un código válido de la app o de recuperación.</p>
            <div>
              <label htmlFor="disable-pwd" className="block text-[11px] font-bold uppercase tracking-[0.14em] text-onix-silver mb-2">
                Contraseña
              </label>
              <input
                data-testid="admin-2fa-disable-password"
                id="disable-pwd"
                type="password"
                className="onix-input"
                value={disablePwd}
                onChange={(e) => setDisablePwd(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div>
              <label htmlFor="disable-code" className="block text-[11px] font-bold uppercase tracking-[0.14em] text-onix-silver mb-2">
                Código 2FA o de recuperación
              </label>
              <input
                data-testid="admin-2fa-disable-code"
                id="disable-code"
                type="text"
                inputMode="numeric"
                maxLength={20}
                className="onix-input max-w-[260px] tracking-[0.3em]"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                required
              />
            </div>
            <button
              data-testid="admin-2fa-disable-submit"
              type="submit"
              disabled={loading || !disablePwd || disableCode.trim().length < 6}
              className="btn-onix-secondary disabled:opacity-60"
            >
              {loading ? "Desactivando…" : "Desactivar 2FA"}
            </button>
          </form>
        </div>
      )}
      {status && phase === "status" && (
        <form onSubmit={changePassword} className="border border-onix-line bg-onix-black2 p-6 space-y-5">
          <div className="flex items-center gap-3">
            <KeyRound size={18} className="text-onix-white" aria-hidden="true" />
            <p className="text-sm font-bold text-onix-white">Cambiar contraseña</p>
          </div>
          <p className="text-xs text-onix-gray leading-relaxed">
            Mínimo 12 caracteres con letras y números. Al cambiarla, las sesiones anteriores quedarán invalidadas.
          </p>
          <div>
            <label htmlFor="cur-pwd" className="block text-[11px] font-bold uppercase tracking-[0.14em] text-onix-silver mb-2">
              Contraseña actual
            </label>
            <input
              data-testid="admin-pwd-current"
              id="cur-pwd"
              type="password"
              className="onix-input"
              value={curPwd}
              onChange={(e) => setCurPwd(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="new-pwd" className="block text-[11px] font-bold uppercase tracking-[0.14em] text-onix-silver mb-2">
                Nueva contraseña
              </label>
              <input
                data-testid="admin-pwd-new"
                id="new-pwd"
                type="password"
                className="onix-input"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                autoComplete="new-password"
                minLength={12}
                required
              />
            </div>
            <div>
              <label htmlFor="confirm-pwd" className="block text-[11px] font-bold uppercase tracking-[0.14em] text-onix-silver mb-2">
                Confirmar nueva contraseña
              </label>
              <input
                data-testid="admin-pwd-confirm"
                id="confirm-pwd"
                type="password"
                className="onix-input"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                autoComplete="new-password"
                minLength={12}
                required
              />
            </div>
          </div>
          {status.enabled && (
            <div>
              <label htmlFor="pwd-code" className="block text-[11px] font-bold uppercase tracking-[0.14em] text-onix-silver mb-2">
                Código 2FA o de recuperación
              </label>
              <input
                data-testid="admin-pwd-code"
                id="pwd-code"
                type="text"
                inputMode="numeric"
                maxLength={20}
                className="onix-input max-w-[260px] tracking-[0.3em]"
                value={pwdCode}
                onChange={(e) => setPwdCode(e.target.value)}
                required
              />
            </div>
          )}
          {pwdError && (
            <p data-testid="admin-pwd-error" role="alert" className="text-xs font-bold text-onix-white border border-dashed border-onix-silver p-3">
              {pwdError}
            </p>
          )}
          {pwdOk && (
            <p data-testid="admin-pwd-success" role="status" className="text-xs font-bold text-onix-white border border-onix-white p-3">
              Contraseña actualizada correctamente. Tu sesión actual sigue activa.
            </p>
          )}
          <button
            data-testid="admin-pwd-submit"
            type="submit"
            disabled={pwdLoading || !curPwd || !newPwd || !confirmPwd || (status.enabled && pwdCode.trim().length < 6)}
            className="btn-onix-primary disabled:opacity-60"
          >
            {pwdLoading ? "Cambiando…" : "Cambiar contraseña"}
          </button>
        </form>
      )}
    </div>
  );
}
